import React, { useEffect, useState } from "react";
import { DeviceMobile, X, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  canInstall, triggerInstall, isIOS, isStandalone, onInstallAvailabilityChange,
} from "../lib/install";

/** Small reusable "Install App" pill — auto-hides if already installed or not installable. */
export const InstallAppButton = ({ className = "", small = false, testid = "install-app-btn" }) => {
  const [installable, setInstallable] = useState(canInstall());
  const [ios] = useState(isIOS());

  useEffect(() => onInstallAvailabilityChange(setInstallable), []);

  if (isStandalone()) return null;
  if (!installable && !ios) return null;

  const handleClick = async () => {
    if (ios) {
      toast.info("On iPhone: tap the Share button, then 'Add to Home Screen'.", { duration: 7000 });
      return;
    }
    const { outcome } = await triggerInstall();
    if (outcome === "accepted") toast.success("PitchIQ added to your home screen!");
    if (outcome === "dismissed") toast("No worries — you can install any time from your profile.");
  };

  const base = small
    ? "px-2.5 py-1 text-[10px]"
    : "px-3 py-1.5 text-xs";

  return (
    <button
      data-testid={testid}
      onClick={handleClick}
      className={`${base} uppercase tracking-widest text-primary-yellow border border-primary-yellow/30 hover:bg-primary-yellow/10 hover:border-primary-yellow font-bold flex items-center gap-1.5 transition-colors ${className}`}
    >
      <DeviceMobile size={14} weight="bold" />
      Install App
    </button>
  );
};

/** Show a one-time install nudge right after login. Stores a flag in localStorage. */
export const offerInstallAfterLogin = () => {
  if (isStandalone()) return;
  if (!canInstall() && !isIOS()) return;
  try {
    if (localStorage.getItem("pitchiq_install_offered") === "1") return;
    localStorage.setItem("pitchiq_install_offered", "1");
  } catch { /* noop */ }

  // small delay so it appears after the navigation, not during it
  setTimeout(() => {
    toast.custom((t) => (
      <div className="bg-[#0F1116] border border-primary-yellow/40 p-4 max-w-sm shadow-xl flex gap-3 items-start">
        <Lightning size={20} weight="fill" className="text-primary-yellow mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-heading text-sm uppercase font-black">Save PitchIQ to your device</div>
          <p className="text-xs text-white/60 mt-1 leading-snug">One-tap access from your home screen — no app store needed.</p>
          <div className="mt-3 flex gap-2">
            <button
              data-testid="install-toast-accept"
              onClick={async () => {
                toast.dismiss(t);
                if (isIOS()) {
                  toast.info("On iPhone: tap the Share button, then 'Add to Home Screen'.", { duration: 7000 });
                } else {
                  const { outcome } = await triggerInstall();
                  if (outcome === "accepted") toast.success("Installed!");
                }
              }}
              className="bg-primary-yellow text-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5"
            >Install</button>
            <button
              data-testid="install-toast-dismiss"
              onClick={() => toast.dismiss(t)}
              className="text-white/50 hover:text-white text-[10px] uppercase tracking-widest"
            >Maybe later</button>
          </div>
        </div>
        <button onClick={() => toast.dismiss(t)} aria-label="Close" className="text-white/40 hover:text-white">
          <X size={16} />
        </button>
      </div>
    ), { duration: 12000 });
  }, 900);
};

/** Full welcome modal shown after registration. */
export const WelcomeInstallModal = ({ open, onClose, name }) => {
  if (!open || isStandalone()) return null;
  const ios = isIOS();
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur grid place-items-center p-4" data-testid="welcome-install-modal">
      <div className="surface border border-primary-yellow/40 max-w-md w-full p-8 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white" aria-label="Close">
          <X size={20} />
        </button>
        <div className="text-center">
          <DeviceMobile size={40} weight="duotone" className="text-primary-yellow mx-auto" />
          <h2 className="font-heading text-2xl uppercase font-black mt-4">Welcome{name ? `, ${name.split(" ")[0]}` : ""}</h2>
          <p className="text-white/60 mt-2 text-sm">Save PitchIQ to your home screen for matchday alerts, one-tap predictions, and a full-screen app experience.</p>
        </div>
        <div className="mt-6 flex flex-col gap-2">
          {ios ? (
            <div className="text-[11px] text-white/60 leading-relaxed bg-[#0B0D11] border border-white/10 p-4">
              <strong className="text-primary-yellow">On iPhone:</strong> tap the <strong>Share</strong> button in Safari, then <strong>&quot;Add to Home Screen&quot;</strong>.
            </div>
          ) : (
            <button
              data-testid="welcome-install-btn"
              onClick={async () => {
                const { outcome } = await triggerInstall();
                if (outcome === "accepted") toast.success("Installed!");
                onClose();
              }}
              className="bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 w-full"
            >
              Install PitchIQ
            </button>
          )}
          <button
            data-testid="welcome-install-skip"
            onClick={onClose}
            className="text-white/50 hover:text-white text-[10px] uppercase tracking-widest py-2"
          >Skip for now</button>
        </div>
      </div>
    </div>
  );
};
